import React from 'react';
import {
  IconButton,
  Box,
  Badge,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  useBreakpointValue,
  HStack,
  Text,
  Button,
} from '@chakra-ui/react';
import { BellIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationContext } from './NotificationProvider';
import {
  useNotificationMutations,
  useNotificationList,
  useNotificationSummary,
  flattenNotificationPages,
} from '../../hooks/useNotifications';
import NotificationList from './NotificationList';
import NotificationPopover from './NotificationPopover';
import { resolveRowOpenTarget } from '../../lib/notificationDeepLink';
import { useNotificationQuiet } from '../../hooks/useNotificationQuiet';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import type { NotificationItem } from '../../types/notification';

function badgeLabel(count: number): string | null {
  if (count <= 0) return null;
  if (count > 99) return '99+';
  return String(count);
}

export default function NotificationBell(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId, enabled, registerOpenPanel } = useNotificationContext();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const popover = useDisclosure();
  const drawer = useDisclosure();
  const [status, setStatus] = React.useState<'all' | 'unread' | 'archived'>('all');

  const { data: summary } = useNotificationSummary(userId, enabled);
  const list = useNotificationList(userId, { status, limit: 20 }, enabled);
  const items = flattenNotificationPages(list.data?.pages);
  const mutations = useNotificationMutations(userId);
  const { data: preferenceData } = useNotificationPreferences(userId, enabled);
  const quiet = useNotificationQuiet(preferenceData?.config);

  const openPanel = React.useCallback(() => {
    if (isMobile) drawer.onOpen();
    else popover.onOpen();
  }, [isMobile, drawer, popover]);

  React.useEffect(() => {
    if (!enabled) {
      registerOpenPanel(null);
      return () => registerOpenPanel(null);
    }
    registerOpenPanel(openPanel);
    return () => registerOpenPanel(null);
  }, [enabled, registerOpenPanel, openPanel]);

  if (!enabled) {
    return null;
  }

  const unread = summary?.unreadCount ?? 0;

  // `hideBadgeWhileQuiet` hides the badge only; the unread count stays truthful
  // in the accessible label so a screen reader still announces it.
  const hideBadge = quiet.hidden;
  const label = badgeLabel(unread);

  const markRead = (item: NotificationItem) => {
    if (item.readAt == null) mutations.setRead.mutate({ id: item.id, read: true });
  };
  const toggleRead = (item: NotificationItem) => {
    mutations.setRead.mutate({ id: item.id, read: item.readAt == null });
  };
  const openItem = (item: NotificationItem) => {
    markRead(item);
    const target = resolveRowOpenTarget(item);
    if (target) {
      popover.onClose();
      drawer.onClose();
      navigate(target.path);
    }
  };
  const archive = (item: NotificationItem) => {
    mutations.setArchived.mutate({ id: item.id, archived: true });
  };
  const readAll = () => mutations.markAllRead.mutate(summary?.createdRevision ?? '0');

  const trigger = (
    <IconButton
      aria-label={t('notification.bellLabel', { count: unread })}
      icon={
        <Box position="relative">
          <BellIcon boxSize="20px" />
          {label && !hideBadge && (
            <Badge
              data-testid="notification-badge"
              position="absolute"
              top="-6px"
              right="-8px"
              colorScheme="red"
              borderRadius="full"
              fontSize="10px"
              px={1}
              minW="16px"
              textAlign="center"
            >
              {label}
            </Badge>
          )}
        </Box>
      }
      variant="ghost"
      size={{ base: 'sm', md: 'md' }}
      minW="44px"
      h="44px"
      onClick={openPanel}
    />
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} placement="right" size="sm">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader borderBottomWidth="1px">
              <HStack justify="space-between">
                <Text>{t('notification.title')}</Text>
                <Button size="xs" variant="ghost" onClick={readAll}>
                  {t('notification.readAll')}
                </Button>
              </HStack>
            </DrawerHeader>
            <DrawerBody p={0}>
              <NotificationList
                items={items}
                isLoading={list.isLoading}
                isError={list.isError}
                onRetry={() => list.refetch()}
                onItemOpen={openItem}
                onItemToggleRead={toggleRead}
                onItemArchive={archive}
                status={status}
                onStatusChange={setStatus}
                maxHeight="70vh"
              />
              <Box p={3}>
                <Button
                  size="sm"
                  w="full"
                  variant="outline"
                  onClick={() => {
                    drawer.onClose();
                    navigate('/notifications');
                  }}
                >
                  {t('notification.viewAll')}
                </Button>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <NotificationPopover
      isOpen={popover.isOpen}
      onClose={popover.onClose}
      trigger={trigger}
      items={items}
      isLoading={list.isLoading}
      isError={list.isError}
      onRetry={() => list.refetch()}
      onItemOpen={openItem}
      onItemToggleRead={toggleRead}
      onItemArchive={archive}
      status={status}
      onStatusChange={setStatus}
      onReadAll={readAll}
    />
  );
}
